(() => {
  const locale = document.documentElement.lang === 'ar' ? 'ar-MA' : 'fr-MA';
  const numberFormatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });

  const normalizePhone = (value) => {
    let digits = String(value || '').replace(/[^\d+]/g, '');
    if (digits.startsWith('00212')) digits = `+212${digits.slice(5)}`;
    else if (digits.startsWith('212')) digits = `+${digits}`;
    else if (/^0[5-7]\d{8}$/.test(digits)) digits = `+212${digits.slice(1)}`;
    if (!/^\+212[5-7]\d{8}$/.test(digits)) return null;
    return /^(\d)\1{8}$/.test(digits.slice(4)) ? null : digits;
  };

  const setBodyLock = (locked) => {
    document.body.classList.toggle('opla-cod-open', locked);
  };

  const focusableElements = (container) => [...container.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )].filter((element) => !element.hidden && element.offsetParent !== null);

  document.querySelectorAll('[data-opla-cod]').forEach((root) => {
    const form = root.querySelector('[data-opla-cod-form]');
    const quantity = root.querySelector('[data-opla-cod-quantity]');
    const alert = root.querySelector('[data-opla-cod-alert]');
    const submit = root.querySelector('[data-opla-cod-submit]');
    const success = root.querySelector('[data-opla-cod-success]');
    const priceMinor = Number(root.dataset.priceMinor || 0);
    let idempotencyKey = null;
    let formStartedAt = Date.now();

    if (!form || !quantity || !alert || !submit || !success || !priceMinor) return;

    const totalAmount = () => (priceMinor * Number(quantity.value || 1)) / 100;
    const updateTotal = () => {
      const formatted = `${numberFormatter.format(totalAmount())} ${root.dataset.priceUnit}`;
      root.querySelectorAll('[data-opla-cod-total]').forEach((node) => { node.textContent = formatted; });
    };

    const clearErrors = () => {
      alert.hidden = true;
      alert.textContent = '';
      root.querySelectorAll('[data-opla-field]').forEach((field) => {
        field.classList.remove('has-error');
        field.querySelector('input, select, textarea')?.removeAttribute('aria-invalid');
      });
      root.querySelectorAll('[data-opla-error-for]').forEach((error) => {
        error.hidden = true;
        error.textContent = '';
      });
    };

    const fieldError = (name, message) => {
      const wrapper = root.querySelector(`[data-opla-field="${name}"]`);
      const error = root.querySelector(`[data-opla-error-for="${name}"]`);
      const control = wrapper?.querySelector('input, select, textarea');
      wrapper?.classList.add('has-error');
      control?.setAttribute('aria-invalid', 'true');
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    };

    const showAlert = (message) => {
      alert.textContent = message;
      alert.hidden = false;
      alert.focus?.();
    };

    const validate = () => {
      clearErrors();
      let valid = true;
      const requiredFields = ['fullName', 'city'];
      requiredFields.forEach((name) => {
        const input = form.elements.namedItem(name);
        const value = input && 'value' in input ? input.value.trim() : '';
        if (value.length < 2 || /[<>]/.test(value)) {
          fieldError(name, root.dataset.errorRequired);
          valid = false;
        }
      });
      const address = form.elements.namedItem('address1')?.value.trim() || '';
      if (address && (address.length < 8 || /[<>]/.test(address))) {
        fieldError('address1', root.dataset.errorRequired);
        valid = false;
      }
      if (!normalizePhone(form.elements.namedItem('phone')?.value)) {
        fieldError('phone', root.dataset.errorPhone);
        valid = false;
      }
      return valid;
    };

    const resetTurnstile = () => {
      if (window.turnstile && typeof window.turnstile.reset === 'function') {
        try { window.turnstile.reset(); } catch (_) { /* Widget may still be loading. */ }
      }
    };

    const showSuccess = async (result) => {
      form.hidden = true;
      root.querySelector('.opla-cod__order')?.setAttribute('hidden', '');
      root.querySelector('.opla-cod__heading')?.setAttribute('hidden', '');
      const reference = success.querySelector('[data-opla-cod-reference]');
      if (reference) reference.textContent = result.reference;
      success.hidden = false;
      success.focus();

      document.dispatchEvent(new CustomEvent('opla:cod-lead', {
        detail: {
          eventId: result.eventId,
          reference: result.reference,
          value: Number(result.total?.amount || totalAmount()),
          currency: result.total?.currency || 'MAD',
          source: root.dataset.source,
        },
      }));

      if (root.dataset.source === 'cart') {
        try {
          await fetch(`${window.Shopify?.routes?.root || '/'}cart/clear.js`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
          });
        } catch (_) {
          // The Shopify draft exists already; cart clearing is best effort only.
        }
      }
    };

    quantity.addEventListener('change', updateTotal);
    updateTotal();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!validate()) return;

      const turnstileToken = form.querySelector('[name="cf-turnstile-response"]')?.value || '';
      if (root.dataset.turnstileEnabled === 'true' && !turnstileToken) {
        showAlert(root.dataset.errorVerification);
        return;
      }

      idempotencyKey ||= crypto.randomUUID();
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      form.setAttribute('aria-busy', 'true');
      clearErrors();

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15_000);
      const payload = {
        idempotencyKey,
        quantity: Number(quantity.value),
        customer: {
          fullName: form.elements.namedItem('fullName').value,
          phone: form.elements.namedItem('phone').value,
          city: form.elements.namedItem('city').value,
          address1: form.elements.namedItem('address1').value,
          note: form.elements.namedItem('note')?.value || '',
        },
        website: form.elements.namedItem('website').value,
        locale: document.documentElement.lang === 'ar' ? 'ar' : 'fr',
        source: root.dataset.source,
        formVersion: root.dataset.formVersion,
        formStartedAt,
        turnstileToken,
      };

      try {
        const response = await fetch(root.dataset.endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'same-origin',
          signal: controller.signal,
        });
        const result = await response.json().catch(() => ({}));

        if (response.ok && result.ok) {
          await showSuccess(result);
          return;
        }

        if (result.code === 'validation_error' && result.fields) {
          Object.keys(result.fields).forEach((name) => {
            if (name !== 'form') fieldError(name, name === 'phone' ? root.dataset.errorPhone : root.dataset.errorRequired);
          });
          showAlert(root.dataset.errorRequired);
        } else if (result.code === 'verification_failed') showAlert(root.dataset.errorVerification);
        else if (result.code === 'rate_limited') showAlert(root.dataset.errorRate);
        else if (result.code === 'unavailable') showAlert(root.dataset.errorUnavailable);
        else if (result.code === 'processing') showAlert(root.dataset.errorProcessing);
        else showAlert(root.dataset.errorGeneric);
        resetTurnstile();
      } catch (error) {
        showAlert(error?.name === 'AbortError' ? root.dataset.errorNetwork : root.dataset.errorGeneric);
        resetTurnstile();
      } finally {
        window.clearTimeout(timeout);
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        form.removeAttribute('aria-busy');
      }
    });

    root.addEventListener('opla:cod-reset-start', () => { formStartedAt = Date.now(); });
  });

  document.querySelectorAll('[data-opla-open-cod]').forEach((button) => {
    button.addEventListener('click', () => {
      const id = button.dataset.oplaCodTarget;
      const modal = [...document.querySelectorAll('[data-opla-cod-modal]')]
        .find((candidate) => candidate.dataset.oplaCodModal === id);
      if (!modal) return;

      const codRoot = modal.querySelector('[data-opla-cod]');
      if (codRoot) codRoot.dataset.source = button.dataset.oplaCodSource || codRoot.dataset.source;

      const sourceQuantity = button.dataset.oplaQuantitySelector
        ? document.querySelector(button.dataset.oplaQuantitySelector)
        : null;
      const drawerQuantity = modal.querySelector('[data-opla-cod-quantity]');
      if (sourceQuantity && drawerQuantity) drawerQuantity.value = String(Math.min(5, Math.max(1, Number(sourceQuantity.value) || 1)));
      drawerQuantity?.dispatchEvent(new Event('change', { bubbles: true }));

      modal.hidden = false;
      modal.dataset.returnFocus = button.id || '';
      setBodyLock(true);
      codRoot?.dispatchEvent(new CustomEvent('opla:cod-reset-start'));
      modal.querySelector('input:not([type="hidden"])')?.focus();
      document.dispatchEvent(new CustomEvent('opla:cod-form-open', { detail: { source: button.dataset.oplaCodSource || 'product' } }));
    });
  });

  const closeModal = (modal) => {
    modal.hidden = true;
    setBodyLock(false);
    const returnFocus = modal.dataset.returnFocus && document.getElementById(modal.dataset.returnFocus);
    returnFocus?.focus();
  };

  document.querySelectorAll('[data-opla-cod-modal]').forEach((modal) => {
    modal.querySelectorAll('[data-opla-cod-close]').forEach((button) => {
      button.addEventListener('click', () => closeModal(modal));
    });
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeModal(modal);
      if (event.key !== 'Tab') return;
      const focusable = focusableElements(modal);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  });
})();
